import { Injectable } from '@angular/core';
import { ControlRect, FaceLandmark, NoseControlRect, LIFE_TEST_ID, POL_FACE_TOO_FAR_MIN_HEIGHT_RATIO } from '../../models/life-check';
import { NormalizedLandmark } from '@mediapipe/tasks-vision';
import { IPoint } from '../../models/user-identification';

@Injectable({
	providedIn: 'root',
})
export class PositionFaceService {
	// Draw
	private draw: boolean = false;

	// Points
	private foreheadPoint: IPoint;
	private chinPoint: IPoint;
	private nosePoint: IPoint;
	private rightEarPoint: IPoint;
	private leftEarPoint: IPoint;
	private noseCenterPoint: IPoint;

	// Control rects
	private controlRect: IPoint;
	private externalControlRect: IPoint;
	private noseControlRect: IPoint;

	/** Falso tras `invalidateFaceLandmarks()` hasta el próximo `setFacePoints` (evita geometría obsoleta). */
	private hasCurrentLandmarks = false;

	public setFacePoints(landmarks: NormalizedLandmark[], canvas: HTMLCanvasElement): void {
		this.nosePoint = {
			x: landmarks[FaceLandmark.Nose].x * canvas.width,
			y: landmarks[FaceLandmark.Nose].y * canvas.height,
			height: this.draw ? 1 : 0,
			width: this.draw ? 1 : 0,
		};
		this.foreheadPoint = {
			x: landmarks[FaceLandmark.Forehead].x * canvas.width,
			y: landmarks[FaceLandmark.Forehead].y * canvas.height,
			height: this.draw ? 1 : 0,
			width: this.draw ? 1 : 0,
		};
		this.chinPoint = {
			x: landmarks[FaceLandmark.Chin].x * canvas.width,
			y: landmarks[FaceLandmark.Chin].y * canvas.height,
			height: this.draw ? 1 : 0,
			width: this.draw ? 1 : 0,
		};
		this.leftEarPoint = {
			x: landmarks[FaceLandmark.LeftEar].x * canvas.width,
			y: landmarks[FaceLandmark.LeftEar].y * canvas.height,
			height: this.draw ? 1 : 0,
			width: this.draw ? 1 : 0,
		};
		this.rightEarPoint = {
			x: landmarks[FaceLandmark.RightEar].x * canvas.width,
			y: landmarks[FaceLandmark.RightEar].y * canvas.height,
			height: this.draw ? 1 : 0,
			width: this.draw ? 1 : 0,
		};
		this.hasCurrentLandmarks = true;
	}

	/** Cuando MediaPipe no devuelve cara en este frame: no usar puntos del fotograma anterior. */
	public invalidateFaceLandmarks(): void {
		this.hasCurrentLandmarks = false;
	}

	public setNoseCenterPoint(nosePath: Array<IPoint>): void {
		if (nosePath?.length) {
			const auxPoint: IPoint = { x: 0, y: 0, width: 1, height: 1 };
			nosePath.forEach((nosePoint: IPoint) => {
				auxPoint.x += nosePoint.x;
				auxPoint.y += nosePoint.y;
			});
			this.noseCenterPoint = {
				x: auxPoint.x / nosePath.length,
				y: auxPoint.y / nosePath.length,
				width: 1,
				height: 1,
			};
			nosePath = [];
		}
	}

	public centerMatch(): boolean {
		if (!this.hasCurrentLandmarks) {
			return false;
		}
		return (
			this.isPointInRect(this.nosePoint, this.controlRect) &&
			((this.isPointBelowRect(this.chinPoint, this.controlRect) &&
				this.isPointRightRect(this.rightEarPoint, this.controlRect) &&
				this.isPointLeftRect(this.leftEarPoint, this.controlRect)) ||
				(this.isPointAboveRect(this.foreheadPoint, this.controlRect) &&
					this.isPointRightRect(this.rightEarPoint, this.controlRect) &&
					this.isPointLeftRect(this.leftEarPoint, this.controlRect)) ||
				(this.isPointAboveRect(this.foreheadPoint, this.controlRect) &&
					this.isPointBelowRect(this.chinPoint, this.controlRect) &&
					this.isPointLeftRect(this.leftEarPoint, this.controlRect)) ||
				(this.isPointAboveRect(this.foreheadPoint, this.controlRect) &&
					this.isPointBelowRect(this.chinPoint, this.controlRect) &&
					this.isPointRightRect(this.rightEarPoint, this.controlRect))) &&
			this.isPointInRect(this.foreheadPoint, this.externalControlRect) &&
			this.isPointInRect(this.chinPoint, this.externalControlRect) &&
			this.isPointInRect(this.rightEarPoint, this.externalControlRect) &&
			this.isPointInRect(this.leftEarPoint, this.externalControlRect)
		);
	}

	public middleFaceMatch(): boolean {
		if (!this.hasCurrentLandmarks) {
			return false;
		}
		return (
			this.isPointInRect(this.nosePoint, this.controlRect) &&
			this.isPointBelowRect(this.chinPoint, this.controlRect) &&
			this.isPointRightRect(this.rightEarPoint, this.controlRect) &&
			this.isPointLeftRect(this.leftEarPoint, this.controlRect) &&
			this.isPointAboveRect(this.foreheadPoint, this.controlRect) &&
			this.isPointInRect(this.foreheadPoint, this.externalControlRect) &&
			this.isPointInRect(this.chinPoint, this.externalControlRect) &&
			this.isPointInRect(this.rightEarPoint, this.externalControlRect) &&
			this.isPointInRect(this.leftEarPoint, this.externalControlRect)
		);
	}

	public horizontalMatch(): boolean {
		if (!this.hasCurrentLandmarks) {
			return false;
		}
		return (
			this.isPointInRect(this.nosePoint, this.controlRect) &&
			this.isPointAboveRect(this.foreheadPoint, this.controlRect) &&
			this.isPointBelowRect(this.chinPoint, this.controlRect) &&
			this.isPointInRect(this.rightEarPoint, this.controlRect) &&
			this.isPointInRect(this.leftEarPoint, this.controlRect) &&
			this.isPointInRect(this.foreheadPoint, this.externalControlRect) &&
			this.isPointInRect(this.chinPoint, this.externalControlRect) &&
			this.isPointInRect(this.rightEarPoint, this.externalControlRect) &&
			this.isPointInRect(this.leftEarPoint, this.externalControlRect)
		);
	}

	public verticalMatch(): boolean {
		if (!this.hasCurrentLandmarks) {
			return false;
		}
		return (
			this.isPointInRect(this.nosePoint, this.controlRect) &&
			this.isPointInRect(this.foreheadPoint, this.controlRect) &&
			this.isPointInRect(this.chinPoint, this.controlRect) &&
			(this.isPointRightRect(this.rightEarPoint, this.controlRect) ||
				this.isPointLeftRect(this.leftEarPoint, this.controlRect)) &&
			this.isPointInRect(this.foreheadPoint, this.externalControlRect) &&
			this.isPointInRect(this.chinPoint, this.externalControlRect) &&
			this.isPointInRect(this.rightEarPoint, this.externalControlRect) &&
			this.isPointInRect(this.leftEarPoint, this.externalControlRect)
		);
	}

	public isFaceInExternalControlRect(): boolean {
		if (!this.hasCurrentLandmarks) {
			return false;
		}
		return (
			this.isPointInRect(this.foreheadPoint, this.externalControlRect) &&
			this.isPointInRect(this.chinPoint, this.externalControlRect) &&
			this.isPointInRect(this.rightEarPoint, this.externalControlRect) &&
			this.isPointInRect(this.leftEarPoint, this.externalControlRect)
		);
	}

	public isFaceTooFarByVerticalSpan(minHeightRatio: number): boolean {
		if (!this.hasCurrentLandmarks || !this.externalControlRect?.height) {
			return false;
		}
		if (!this.isFaceInExternalControlRect()) {
			return false;
		}
		const verticalSpan = Math.abs(this.chinPoint.y - this.foreheadPoint.y);
		return verticalSpan / this.externalControlRect.height < minHeightRatio;
	}

	public isPolTooFar(hasFaceDetection: boolean, isMatched: boolean): boolean {
		if (!hasFaceDetection || isMatched) {
			return false;
		}
		return this.isFaceTooFarByVerticalSpan(POL_FACE_TOO_FAR_MIN_HEIGHT_RATIO);
	}

	public isPolTooClose(hasFaceDetection: boolean): boolean {
		return hasFaceDetection && !this.isFaceInExternalControlRect();
	}

	public isPolOffCenter(hasFaceDetection: boolean, isMatched: boolean): boolean {
		if (!hasFaceDetection || isMatched) {
			return false;
		}
		return !this.isPolTooFar(hasFaceDetection, isMatched) && !this.isPolTooClose(hasFaceDetection);
	}

	/*
	 * Configuración de las emociones
	 */
	public setControlRect(type: LIFE_TEST_ID, canvas: HTMLCanvasElement): void {
		if (canvas) {
			let widthRect = canvas.width;
			let heightRect = canvas.height;
			const aspectRatio = 336 / 528;

			if (widthRect > heightRect) {
				widthRect = heightRect * aspectRatio;
			} else {
				heightRect = widthRect / aspectRatio;
			}

			// External rect ocupa todo el tec-scan (canvas dimensions)
			this.externalControlRect = {
				x: canvas.width / 2 - widthRect / 2,
				y: canvas.height / 2 - heightRect / 2,
				width: widthRect,
				height: heightRect,
			};

			const lifeTest = {
				[LIFE_TEST_ID.MIDDLEFACE]: () => {
					this.centerControlRect(canvas);
					this.noseControlRect = null;
				},
				[LIFE_TEST_ID.SMILE]: () => {
					this.centerControlRect(canvas);
					this.noseControlRect = null;
				},
				[LIFE_TEST_ID.APPROACHFACE]: () => {
					this.centerControlRect(canvas);
					this.noseControlRect = null;
				},
				[LIFE_TEST_ID.TURNRIGHTHEAD]: () => {
					this.horizontalControlRect(canvas);
					this.setNoiseRect(type);
				},
				[LIFE_TEST_ID.TURNLEFTHEAD]: () => {
					this.horizontalControlRect(canvas);
					this.setNoiseRect(type);
				},
				[LIFE_TEST_ID.LIFTHEAD]: () => {
					this.verticalControlRect(canvas);
					this.setNoiseRect(type);
				},
				[LIFE_TEST_ID.LOWERHEAD]: () => {
					this.verticalControlRect(canvas);
					this.setNoiseRect(type);
				},
			};
			lifeTest[type]();
		}
	}

	private centerControlRect(canvas: HTMLCanvasElement): void {
		// 336 / 528
		const rectWidth = (this.externalControlRect.width * ControlRect.Width) / 336;
		const rectHeight = (this.externalControlRect.height * ControlRect.Height) / 528;

		this.controlRect = {
			x: canvas.width / 2 - rectWidth / 2,
			y: canvas.height / 2 - rectHeight / 3,
			width: rectWidth,
			height: rectHeight,
		};
	}

	private horizontalControlRect(canvas: HTMLCanvasElement): void {
		// 336 / 528
		const rectWidth = (this.externalControlRect.width * ControlRect.ExtraWidth) / 336;
		const rectHeight = (this.externalControlRect.height * ControlRect.Height) / 528;

		this.controlRect = {
			x: canvas.width / 2 - rectWidth / 2,
			y: canvas.height / 2 - rectHeight / 3,
			width: rectWidth,
			height: rectHeight,
		};
	}

	private verticalControlRect(canvas: HTMLCanvasElement): void {
		// 336 / 528
		const rectWidth = (this.externalControlRect.width * ControlRect.Width) / 336;
		const rectHeight = (this.externalControlRect.height * ControlRect.ExtraHeight) / 528;

		this.controlRect = {
			x: canvas.width / 2 - rectWidth / 2,
			y: canvas.height / 2 - (rectHeight * 2) / 4,
			width: rectWidth,
			height: rectHeight,
		};
	}

	private setNoiseRect(type: LIFE_TEST_ID): void {
		if (this.noseCenterPoint) {
			// Escalar proporcionalmente como en los otros rects
			const scaleWidth = this.externalControlRect.width / 336;
			const scaleHeight = this.externalControlRect.height / 528;
			const noseWidth = NoseControlRect.Width * scaleWidth;
			const noseLong = NoseControlRect.Long * scaleHeight;
			const noseMargin = NoseControlRect.MarginCenter * scaleWidth;

			const positions = {
				[LIFE_TEST_ID.LIFTHEAD]: () => {
					this.noseControlRect = {
						x: this.noseCenterPoint.x - noseWidth / 2,
						y: this.noseCenterPoint.y - (noseLong - noseMargin),
						width: noseWidth,
						height: noseLong,
					};
				},
				[LIFE_TEST_ID.LOWERHEAD]: () => {
					this.noseControlRect = {
						x: this.noseCenterPoint.x - noseWidth / 2,
						y: this.noseCenterPoint.y - noseMargin,
						width: noseWidth,
						height: noseLong,
					};
				},
				[LIFE_TEST_ID.TURNRIGHTHEAD]: () => {
					this.noseControlRect = {
						x: this.noseCenterPoint.x - noseLong + noseMargin,
						y: this.noseCenterPoint.y - noseWidth / 2,
						width: noseLong,
						height: noseWidth,
					};
				},
				[LIFE_TEST_ID.TURNLEFTHEAD]: () => {
					this.noseControlRect = {
						x: this.noseCenterPoint.x - noseMargin,
						y: this.noseCenterPoint.y - noseWidth / 2,
						width: noseLong,
						height: noseWidth,
					};
				},
			};
			positions[type]();
		}
	}

	public isMatch(type: LIFE_TEST_ID): boolean {
		const allLifeTest = {
			[LIFE_TEST_ID.MIDDLEFACE]: () => this.middleFaceMatch(),
			[LIFE_TEST_ID.SMILE]: () => this.centerMatch(),
			[LIFE_TEST_ID.APPROACHFACE]: () => this.centerMatch(),
			[LIFE_TEST_ID.TURNLEFTHEAD]: () => this.horizontalMatch(),
			[LIFE_TEST_ID.TURNRIGHTHEAD]: () => this.horizontalMatch(),
			[LIFE_TEST_ID.LIFTHEAD]: () => this.verticalMatch(),
			[LIFE_TEST_ID.LOWERHEAD]: () => this.verticalMatch(),
		};
		return allLifeTest[type]();
	}

	public isPointInRect(p: IPoint, r: IPoint): boolean {
		return p?.x >= r?.x && p?.x <= r?.x + r?.width && p?.y >= r?.y && p?.y <= r?.y + r?.height;
	}

	public isPointAboveRect(p: IPoint, r: IPoint): boolean {
		return p?.y < r?.y;
	}

	public isPointBelowRect(p: IPoint, r: IPoint): boolean {
		return p?.y > r?.y + r?.height;
	}

	public isPointRightRect(p: IPoint, r: IPoint): boolean {
		return p?.x < r?.x;
	}

	public isPointLeftRect(p: IPoint, r: IPoint): boolean {
		return p?.x > r?.x + r?.width;
	}

	public drawControl(ctx: CanvasRenderingContext2D): void {
		ctx.strokeStyle = this.draw ? 'green' : 'transparent';
		ctx.lineWidth = 2;
		ctx.strokeRect(this.nosePoint.x, this.nosePoint.y, this.nosePoint.height, this.nosePoint.width);
		ctx.strokeRect(this.foreheadPoint.x, this.foreheadPoint.y, this.foreheadPoint.height, this.foreheadPoint.width);
		ctx.strokeRect(this.chinPoint.x, this.chinPoint.y, this.chinPoint.height, this.chinPoint.width);
		ctx.strokeRect(this.leftEarPoint.x, this.leftEarPoint.y, this.leftEarPoint.height, this.leftEarPoint.width);
		ctx.strokeRect(this.rightEarPoint.x, this.rightEarPoint.y, this.rightEarPoint.height, this.rightEarPoint.width);
		if (this.controlRect) {
			ctx.strokeRect(this.controlRect.x, this.controlRect.y, this.controlRect.width, this.controlRect.height);
		}
		if (this.externalControlRect) {
			ctx.strokeRect(
				this.externalControlRect.x,
				this.externalControlRect.y,
				this.externalControlRect.width,
				this.externalControlRect.height,
			);
		}
		if (this.noseControlRect) {
			ctx.strokeRect(
				this.noseControlRect.x,
				this.noseControlRect.y,
				this.noseControlRect.width,
				this.noseControlRect.height,
			);
		}
		if (this.noseCenterPoint) {
			ctx.strokeRect(
				this.noseCenterPoint.x,
				this.noseCenterPoint.y,
				this.noseCenterPoint.width,
				this.noseCenterPoint.height,
			);
		}
	}

	/**
	 * Dibuja rectángulos de control y marcadores de landmarks sobre un canvas existente.
	 * Pensado solo para capturas diagnósticas (`buildPolDebugOverlayFrame`); no modifica `draw`
	 * ni el flujo normal de la UI (con `draw=false` los puntos en pantalla tienen tamaño 0).
	 */
	public drawDebugOverlay(ctx: CanvasRenderingContext2D): void {
		ctx.strokeStyle = 'green';
		ctx.lineWidth = 2;
		if (this.hasCurrentLandmarks) {
			this.strokeLandmarkMarker(ctx, this.nosePoint);
			this.strokeLandmarkMarker(ctx, this.foreheadPoint);
			this.strokeLandmarkMarker(ctx, this.chinPoint);
			this.strokeLandmarkMarker(ctx, this.leftEarPoint);
			this.strokeLandmarkMarker(ctx, this.rightEarPoint);
		}
		if (this.controlRect) {
			ctx.strokeRect(this.controlRect.x, this.controlRect.y, this.controlRect.width, this.controlRect.height);
		}
		if (this.externalControlRect) {
			ctx.strokeRect(
				this.externalControlRect.x,
				this.externalControlRect.y,
				this.externalControlRect.width,
				this.externalControlRect.height,
			);
		}
		if (this.noseControlRect) {
			ctx.strokeRect(
				this.noseControlRect.x,
				this.noseControlRect.y,
				this.noseControlRect.width,
				this.noseControlRect.height,
			);
		}
		if (this.noseCenterPoint) {
			this.strokeLandmarkMarker(ctx, this.noseCenterPoint);
		}
	}

	/** Cuadrado verde de 4×4 px centrado en un landmark (visible en JPEG de debug). */
	private strokeLandmarkMarker(ctx: CanvasRenderingContext2D, point: IPoint, size = 4): void {
		if (point?.x == null || point?.y == null) {
			return;
		}
		const half = size / 2;
		ctx.strokeRect(point.x - half, point.y - half, size, size);
	}

	// Getter
	// Points
	public getNosePoint(): IPoint {
		return this.nosePoint;
	}

	public getForeheadPoint(): IPoint {
		return this.foreheadPoint;
	}

	public getChinPoint(): IPoint {
		return this.chinPoint;
	}

	public getLeftEarPoint(): IPoint {
		return this.leftEarPoint;
	}

	public getRightEarPoint(): IPoint {
		return this.rightEarPoint;
	}

	public getNoseCenterPoint(): IPoint {
		return this.noseCenterPoint;
	}

	// Rect
	public getNoseControlRect(): IPoint {
		return this.noseControlRect;
	}

	// Draw
	public getDraw(): boolean {
		return this.draw;
	}

	public setDraw(value: boolean): void {
		this.draw = value;
	}
}
